import { 
    CustomPropertyValue,
    EmployeeRecord,
    formatDateDayMonDDYYYY,
    PaidTimeOffAdjustment,
    PaidTimeOffPolicy,
    PTOPolicyAccrualType,
    TimeOffRequest, 
    TimeOffRequestExtended,
    TimeOffRequestStatus
} from "winslow-core";
import { apiGetEmployeeRecord } from "../../../services/EmployeeRecordService";
import { apiGetTimeOffRequestsForEmployeeRecord } from "../../../services/TimeOffRequestService";
import { erGetCPValue } from "../app-employee-records/employeeRecordUtils";
import { apiGetPaidTimeOffPoliciesForWorkspace } from "../../../services/PaidTimeOffPolicyService";
import { apiGetPaidTimeOffAdjustmentsForEmployeeRecord } from "../../../services/PaidTimeOffAdjustmentService";

export type PTOData = {
    employeeRecord: EmployeeRecord;
    timeOffRequests: TimeOffRequest[];
    paidTimeOffAdjustments: PaidTimeOffAdjustment[];
    accrualMap: Map<string, number>;
    operationsMap: Map<string, string[]>;
}

// NR** this assumes the er has one policy right now but it needs to be
// refactored to accomodate for multiple policies like you're taking a sick
// day vs a vacation day or a bereavement day
//
// this also needs to calculate year over year caps and limits 
// damn this is messy lol

export const getPTODataForEmployeeRecord = async (
    employeeRecordId: string,
    policyId: string | undefined,
    asOfDate: Date | null
) : 
    Promise<PTOData | null> => {

    console.log("[getPTODataForEmployeeRecord] asOfDate: ", asOfDate);

    const employeeRecord = await apiGetEmployeeRecord(employeeRecordId);

    if (!employeeRecord) {
        return null;
    }

    console.log("[getPTODataForEmployeeRecord] employeeRecord: ", employeeRecord);

    const paidTimeOffPolicies = await apiGetPaidTimeOffPoliciesForWorkspace(employeeRecord?.workspaceId || "");

    console.log("[getPTODataForEmployeeRecord] paidTimeOffPolicies: ", paidTimeOffPolicies);

    if (!paidTimeOffPolicies) {
        return null;
    }

    const timeOffRequests = await apiGetTimeOffRequestsForEmployeeRecord(employeeRecord.id || "");

    console.log("[getPTODataForEmployeeRecord] timeOffRequests: ", timeOffRequests);

    if (!timeOffRequests) {
        return null;
    }

    const adjustments = await apiGetPaidTimeOffAdjustmentsForEmployeeRecord(employeeRecord.id || "");

    console.log("[getPTODataForEmployeeRecord] adjustments: ", adjustments);

    if (!adjustments) {
        return null;
    }

    // figure out balance per policyId
    const accruals : Map<string, number> = new Map<string, number>();
    const calcOperations : Map<string, string[]> = new Map<string, string[]>();

    const properties = JSON.parse(employeeRecord.customProperties || "{}") as CustomPropertyValue[];

    console.log("[getPTODataForEmployeeRecord] properties: ", properties);

    let paidTimeOffPolicyIds : string[] = [];

    console.log("[getPTODataForEmployeeRecord] policyId: ", policyId);

    if (policyId) {
        paidTimeOffPolicyIds = [policyId];
        console.log("[getPTODataForEmployeeRecord] policyId: ", policyId);
    } else {
        paidTimeOffPolicyIds = erGetCPValue(employeeRecord, "system-PTOPolicies");
        console.log("[getPTODataForEmployeeRecord] system-PTOPolicies: ", paidTimeOffPolicyIds);
    }

    console.error("[getPTODataForEmployeeRecord] paidTimeOffPolicyIds: ", paidTimeOffPolicyIds);

    const employeeStartDate = new Date(erGetCPValue(employeeRecord, "system-startDate") || "");
    console.log("[getPTODataForEmployeeRecord] system-startDate: ", employeeStartDate);

    if (paidTimeOffPolicyIds && paidTimeOffPolicyIds.length > 0) {
        paidTimeOffPolicyIds.forEach((paidTimeOffPolicyId: string) => {
            const paidTimeOffPolicy = paidTimeOffPolicies.find((policy) => policy.id === paidTimeOffPolicyId);
           
            console.log("[getPTODataForEmployeeRecord] paidTimeOffPolicy: ", paidTimeOffPolicy);
        
            if (!paidTimeOffPolicy) {
                return;
            }

            console.log("[getPTODataForEmployeeRecord] Calculating accruals for ******: ", paidTimeOffPolicy.name);

             // if unlimited just -1 as we don't need a cal
             if (paidTimeOffPolicy.accrualType === PTOPolicyAccrualType.UNLIMITED) {
                accruals.set(paidTimeOffPolicyId, -1);
                return;
            }

            console.log("[getPTODataForEmployeeRecord] original employeeStartDate: ", employeeStartDate);

            const effectiveDate = new Date(paidTimeOffPolicy.effectiveDate);
            const effectiveStartDate = effectiveDate.getTime() > employeeStartDate.getTime() ? 
                effectiveDate : employeeStartDate;
           
            console.log("[getPTODataForEmployeeRecord] policy effectiveDate: ", effectiveDate);
            console.log("[getPTODataForEmployeeRecord] final employeeStartDate: ", effectiveStartDate);

            if (!paidTimeOffPolicy.byAnniversary) {
                // if its accrued over the year
                const { balance, operations } = getAccruedDaysByYearlyFromStartDateToEndDate(
                    paidTimeOffPolicy,
                    timeOffRequests,
                    adjustments,
                    effectiveStartDate,
                    asOfDate || new Date(),
                );
                accruals.set(
                    paidTimeOffPolicyId,
                    balance
                );
                calcOperations.set(
                    paidTimeOffPolicyId,
                    operations
                );
            }

            // if by anniversary
            if (paidTimeOffPolicy.byAnniversary) {
                const { balance, operations } = getAccruedDaysByAnniversaryFromStartDateToEndDate(
                    paidTimeOffPolicy,
                    timeOffRequests,
                    adjustments,
                    employeeStartDate,
                    effectiveStartDate,
                    asOfDate || new Date(),
                );

                accruals.set(
                    paidTimeOffPolicyId,
                    balance
                );
                calcOperations.set(
                    paidTimeOffPolicyId,
                    operations
                );
            }

            // 
        });
    }

    // const accrued = Array.from(accruals.values())[0] || 0;
    // const balance = accrued - daysThisYearApproved - daysThisYearPending;

    return {
        employeeRecord: employeeRecord,
        timeOffRequests: timeOffRequests,
        paidTimeOffAdjustments: adjustments,
        accrualMap: accruals,
        operationsMap: calcOperations,
    }
}


// do this year by year
// 1. figure out how much has been accrued
// 2. figure out how much has been used
// 3. figure out how much can be rolled over to the next year
            
const getAccruedDaysByYearlyFromStartDateToEndDate = (
    policy: PaidTimeOffPolicy,
    timeOffRequests: TimeOffRequestExtended[],  
    adjustments: PaidTimeOffAdjustment[],
    employeeStartDate: Date,
    asOfEndDate: Date,
) : { balance: number, operations: string[]} => {
    const operations : string[] = [];

    operations.push(makeOperation(`Calculating PTO for ${policy.name}`));
    operations.push(makeOperation(`Accrual type: ${policy.accrualType}`));
    operations.push(makeOperation(`Accrual rate: ${policy.accrualRate}`));
    operations.push(makeOperation(`Carryover cap: ${policy.carryoverCap}`));
    operations.push(makeOperation(`Accrual cap: ${policy.accrualCap}`));
    operations.push(makeOperation(`Employee start date: ${formatDateDayMonDDYYYY(employeeStartDate)}`));
    operations.push(makeOperation(`Calculate as of date: ${formatDateDayMonDDYYYY(asOfEndDate)}`));
    operations.push(makeOperation(`Calculating on a yearly basis`));

    if (policy.accrualType === PTOPolicyAccrualType.UNLIMITED) {
        operations.push(makeOperation(`Unlimited PTO policy, no calculation needed`));
        return {
            balance: -1,
            operations
        };
    }

    let currentYear = employeeStartDate.getFullYear();
    const accruals : Map<number, number> = new Map<number, number>();
    let accruedDays = 0;

    while (currentYear <= asOfEndDate.getFullYear()) {
        
        operations.push(makeOperation(`**************Calculation for ${currentYear}***************`));
        operations.push(makeOperation(`Starting accrual balance: ${accruedDays}`));
        
        // is this the first year
        if (currentYear === employeeStartDate.getFullYear()) {
            accruedDays += getAccruedDaysByYearlyOneYear(policy, employeeStartDate, asOfEndDate);
        } else {
            accruedDays += getAccruedDaysByYearlyOneYear(policy, new Date(currentYear, 0, 1), asOfEndDate);
        }

        operations.push(makeOperation(`Accrued days for ${currentYear}: ${accruedDays}`));

        // now how much has been used
        const torThisYearApproved = timeOffRequests.filter((request: TimeOffRequestExtended) => {
            if (request.paidTimeOffPolicyId !== policy.id) {
                return false;
            }

            // if its pending or approved, we consider it used for the calc
            if (!(request.status === TimeOffRequestStatus.APPROVED || request.status === TimeOffRequestStatus.PENDING)) {
                return false;
            }

            const startDate = new Date(request.startDate);
            const endDate = new Date(request.endDate);

            if (startDate.getTime() > asOfEndDate.getTime()) {
                return false;
            }

            // anything that is totally or partially in this year counts
            return startDate.getFullYear() === currentYear ||
                endDate.getFullYear() === currentYear;
        });

        // figure out how many days are used in this year
        const daysThisYearUsed = torThisYearApproved.reduce((acc, request) => {
            let startDate = new Date(request.startDate);
            if (startDate.getFullYear() < currentYear) {
                startDate = new Date(currentYear, 0, 1);
            }
            let endDate = new Date(request.endDate);
            if (endDate.getFullYear() > currentYear) {
                endDate = new Date(currentYear, 11, 31);
            }
            const diffTime = Math.abs(endDate.getTime() - startDate.getTime());
            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;

            operations.push(makeOperation(`PTO request: ${formatDateDayMonDDYYYY(new Date(request.startDate))} to ${formatDateDayMonDDYYYY(new Date(request.endDate))} - days used: ${diffDays}`));
            return acc + diffDays;
        }, 0);

        operations.push(makeOperation(`PTO taken on this policy this year: ${daysThisYearUsed}`));

        accruedDays -= daysThisYearUsed;

        operations.push(makeOperation(`Balance after PTO taken: ${accruedDays}`));

        // now we need to check if we have any adjustments
        const adjustmentsThisYear = adjustments.filter((adjustment: PaidTimeOffAdjustment) => {
            if (adjustment.paidTimeOffPolicyId !== policy.id) {
                return false;
            }

            const createdDate = new Date(adjustment.createdAt);

            // not inside the range
            if (createdDate.getTime() > asOfEndDate.getTime()) {
                return false;
            }

            // anything that is in this year counts
            return createdDate.getFullYear() === currentYear
        });

        // figure out how many days were adjusted this year
        const adjustmentDays = adjustmentsThisYear.reduce((acc, adjustment) => {
            
            let diffDays = (adjustment.adjustedHours / 8);
            if (!adjustment.isAddition) {
                diffDays = -diffDays;
            }

            operations.push(makeOperation(`Adjustment: ${formatDateDayMonDDYYYY(new Date(adjustment.createdAt))} days: ${diffDays}`));
            return acc + diffDays;
        }, 0);

        operations.push(makeOperation(`Adjustments taken on this policy this year: ${adjustmentDays}`));

        accruedDays += adjustmentDays;

        operations.push(makeOperation(`Balance after adjustments taken: ${accruedDays}`));

        // now we need to figure out how much can be rolled over if we are not the current year
        // if we are the current year, we don't need to worry about this

        if (currentYear !== asOfEndDate.getFullYear()) {
            
            // if we have a carryover cap, we need to check if we can roll over
            if (!(policy.carryoverCap === undefined || policy.carryoverCap === null) && accruedDays > policy.carryoverCap) {
                accruedDays = policy.carryoverCap;
            } else if (accruedDays < 0) {
                accruedDays = 0;
            } 

            operations.push(makeOperation(`Balance after carryover cap applied: ${accruedDays}`));
        }

        operations.push(makeOperation(`Balance before accrual cap: ${accruedDays}`));

        // if we have an accrual cap, we need to check we are under it
        if (!(policy.accrualCap === undefined || policy.accrualCap === null) && accruedDays > policy.accrualCap) {
            accruedDays = policy.accrualCap;
            operations.push(makeOperation(`Balance after accrual cap applied: ${accruedDays}`));
        } 

        operations.push(makeOperation(`Final balance for ${currentYear}: ${accruedDays}`));
        
        accruals.set(currentYear, accruedDays);
        currentYear++;
    }


    // NR> fix this its just temp
    // const daysAccrued = Array.from(accruals.values()).reduce((acc, days) => acc + days, 0);
    // console.log("[getAccruedDaysByYearlyFromStartDate] daysAccrued: ", daysAccrued);
    return {
        balance: accruedDays,
        operations
    };
}

// employee start date is the date active for this year (in the first year it'll be mid year some time)
// but for all others years i'll be jan 1
// figure out if its added up front and then what the pro rated amount is
// or earned over time
const getAccruedDaysByYearlyOneYear = (policy: PaidTimeOffPolicy, employeeStartDate: Date, endDate: Date) => {

    // console.log("[getAccruedDaysByYearlyOneYear] employeeStartDate: ", employeeStartDate);
    console.log(`[getAccruedDaysByYearlyOneYear] from date: ${employeeStartDate.getFullYear()}-${employeeStartDate.getMonth() + 1}-${employeeStartDate.getDate()}`);

    let accrualStartDate = employeeStartDate;

    // assume its a full year
    let accrualEndDate = new Date(employeeStartDate.getFullYear(), 11, 31);

    // but there may be an end date
    if (employeeStartDate.getFullYear() === endDate.getFullYear()) {
        accrualEndDate = endDate;
    } 
    // else if (employeeStartDate.getFullYear() < endDate.getFullYear()) {
    //     const currentDate = new Date();
    //     if (currentDate.getTime() < accrualEndDate.getTime()) {
    //         accrualEndDate = currentDate
    //     }
    // }

    console.log(`[getAccruedDaysByYearlyOneYear] to date: ${accrualEndDate.getFullYear()}-${accrualEndDate.getMonth() + 1}-${accrualEndDate.getDate()}`);


    // calculate the number of days between the start and end date
    const diffTime = Math.abs(accrualEndDate.getTime() - accrualStartDate.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    // this is if earned by day
    let daysAccrued = Math.floor(diffDays / policy.accrualRate);

    // if its given up front
    // 1. if not on Jan 1 (e.g. a start date of 6/1), then we need to pro rate the amount
    // 2. else give it all up front
    if (policy.accrualType === PTOPolicyAccrualType.UPFRONT) {
        // if the start date is not Jan 1, we need to pro rate the amount
        if (employeeStartDate.getMonth() !== 0 || employeeStartDate.getDate() !== 1) {
            daysAccrued = Math.floor((diffDays / 365) * policy.accrualRate);
        } else {
            // if its Jan 1, we give it all up front
            daysAccrued = policy.accrualRate;
        }
    }

    console.log("[getAccruedDaysByYearlyOneYear] days worked this year: ", diffDays);
    console.log("[getAccruedDaysByYearlyOneYear] accrualRate: ", policy.accrualRate);
    console.log("[getAccruedDaysByYearlyOneYear] daysAccrued: ", daysAccrued);
    
    return daysAccrued;
}

   
const makeOperation = (operation: string) => {
    console.log("[makeOperation] operation: ", operation);
    return `Operation: ${operation}`;
}

const sameDateNextYearMinusOneDay = (date = new Date()) => {
    // Clone the input date to avoid mutating it
    const nextYearDate = new Date(date);
    // Set the year to next year
    nextYearDate.setFullYear(nextYearDate.getFullYear() + 1);
    // Subtract one day (in milliseconds)
    nextYearDate.setDate(nextYearDate.getDate() - 1);
    return nextYearDate;
  }


// do this and year anniversary
// 1. figure out how much has been accrued
// 2. figure out how much has been used
// 3. figure out how much can be rolled over to the next year
            
const getAccruedDaysByAnniversaryFromStartDateToEndDate = (
    policy: PaidTimeOffPolicy,
    timeOffRequests: TimeOffRequestExtended[],  
    adjustments: PaidTimeOffAdjustment[],
    employeeStartDate: Date,
    effectiveDate: Date,
    asOfEndDate: Date,
) : { balance: number, operations: string[]} => {
    const operations : string[] = [];

    operations.push(makeOperation(`Calculating PTO for ${policy.name}`));
    operations.push(makeOperation(`Accrual type: ${policy.accrualType}`));
    operations.push(makeOperation(`Accrual rate: ${policy.accrualRate}`));
    operations.push(makeOperation(`Carryover cap: ${policy.carryoverCap}`));
    operations.push(makeOperation(`Accrual cap: ${policy.accrualCap}`));
    operations.push(makeOperation(`Employee start date: ${formatDateDayMonDDYYYY(employeeStartDate)}`));
    operations.push(makeOperation(`Effective date: ${formatDateDayMonDDYYYY(effectiveDate)}`));
    operations.push(makeOperation(`Calculate as of date: ${formatDateDayMonDDYYYY(asOfEndDate)}`));
    operations.push(makeOperation(`Calculating on an annual basis (${formatDateDayMonDDYYYY(employeeStartDate)} - ${formatDateDayMonDDYYYY(sameDateNextYearMinusOneDay(employeeStartDate))})`));

    if (policy.accrualType === PTOPolicyAccrualType.UNLIMITED) {
        operations.push(makeOperation(`Unlimited PTO policy, no calculation needed`));
        return {
            balance: -1,
            operations
        };
    }

    // just for the first year, make sure we start at the effective date or the start date
    // whichever is later
    const firstYearStartDate = effectiveDate.getTime() > employeeStartDate.getTime() ?
        effectiveDate : employeeStartDate;

    let currentYear = firstYearStartDate.getFullYear();
    const accruals : Map<number, number> = new Map<number, number>();
    let accruedDays = 0;

    while (currentYear <= asOfEndDate.getFullYear()) {
        
        operations.push(makeOperation(`**************Calculation for ${currentYear}***************`));
        operations.push(makeOperation(`Starting accrual balance: ${accruedDays}`));

        // lets get the anniversary range before any modifiers
        let anniversaryStartDate = new Date(currentYear, employeeStartDate.getMonth(), employeeStartDate.getDate());
        let anniversaryEndDate = sameDateNextYearMinusOneDay(anniversaryStartDate);

        let thisYearStartDate = anniversaryStartDate;
        if (currentYear === firstYearStartDate.getFullYear()) {
            thisYearStartDate = firstYearStartDate;
        }

        accruedDays += getAccruedDaysByAnniversaryOneYear(
            policy,
            anniversaryStartDate,
            anniversaryEndDate,
            thisYearStartDate,
            asOfEndDate
        );

        operations.push(makeOperation(`Accrued days for ${currentYear}: ${accruedDays}`));

        // now how much has been used
        const torThisYearApproved = timeOffRequests.filter((request: TimeOffRequestExtended) => {
            if (request.paidTimeOffPolicyId !== policy.id) {
                return false;
            }

            // if its pending or approved, we consider it used for the calc
            if (!(request.status === TimeOffRequestStatus.APPROVED || request.status === TimeOffRequestStatus.PENDING)) {
                return false;
            }

            const startDate = new Date(request.startDate);
            const endDate = new Date(request.endDate);

            if (startDate.getTime() > asOfEndDate.getTime()) {
                return false;
            }

            // anything that is totally or partially in this anniversary year
            return startDate.getTime() <= anniversaryEndDate.getTime() &&
                endDate.getTime() >= thisYearStartDate.getTime()
        });

        // figure out how many days are used in this year
        const daysThisYearUsed = torThisYearApproved.reduce((acc, request) => {
            let startDate = new Date(request.startDate);
            if (startDate.getTime() < thisYearStartDate.getTime()) {
                startDate = thisYearStartDate
            }
            let endDate = new Date(request.endDate);
            if (endDate.getTime() > anniversaryEndDate.getTime()) {
                endDate = anniversaryEndDate;
            }
            const diffTime = Math.abs(endDate.getTime() - startDate.getTime());
            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;

            operations.push(makeOperation(`PTO request: ${formatDateDayMonDDYYYY(new Date(request.startDate))} to ${formatDateDayMonDDYYYY(new Date(request.endDate))} - days used: ${diffDays}`));
            return acc + diffDays;
        }, 0);

        operations.push(makeOperation(`PTO taken on this policy this year: ${daysThisYearUsed}`));

        accruedDays -= daysThisYearUsed;

        operations.push(makeOperation(`Balance after PTO taken: ${accruedDays}`));

        // now we need to check if we have any adjustments
        const adjustmentsThisYear = adjustments.filter((adjustment: PaidTimeOffAdjustment) => {
            if (adjustment.paidTimeOffPolicyId !== policy.id) {
                return false;
            }

            const createdDate = new Date(adjustment.createdAt);

            // not inside the range
            if (createdDate.getTime() > asOfEndDate.getTime()) {
                return false;
            }

            // anything that is in this year counts
            return createdDate.getTime() >= thisYearStartDate.getTime() &&
                createdDate.getTime() <= anniversaryEndDate.getTime();
        });

        // figure out how many days were adjusted this year
        const adjustmentDays = adjustmentsThisYear.reduce((acc, adjustment) => {
            
            let diffDays = (adjustment.adjustedHours / 8);
            if (!adjustment.isAddition) {
                diffDays = -diffDays;
            }

            operations.push(makeOperation(`Adjustment: ${formatDateDayMonDDYYYY(new Date(adjustment.createdAt))} days: ${diffDays}`));
            return acc + diffDays;
        }, 0);

        operations.push(makeOperation(`Adjustments taken on this policy this year: ${adjustmentDays}`));

        accruedDays += adjustmentDays;

        operations.push(makeOperation(`Balance after adjustments taken: ${accruedDays}`));

        // now we need to figure out how much can be rolled over if we are not the current year
        // if we are the current year, we don't need to worry about this

        if (currentYear !== asOfEndDate.getFullYear()) {
            
            // if we have a carryover cap, we need to check if we can roll over
            if (!(policy.carryoverCap === undefined || policy.carryoverCap === null) && accruedDays > policy.carryoverCap) {
                accruedDays = policy.carryoverCap;
            } else if (accruedDays < 0) {
                accruedDays = 0;
            } 

            operations.push(makeOperation(`Balance after carryover cap applied: ${accruedDays}`));
        }

        operations.push(makeOperation(`Balance before accrual cap: ${accruedDays}`));

        // if we have an accrual cap, we need to check we are under it
        if (!(policy.accrualCap === undefined || policy.accrualCap === null) && accruedDays > policy.accrualCap) {
            accruedDays = policy.accrualCap;
            operations.push(makeOperation(`Balance after accrual cap applied: ${accruedDays}`));
        } 

        operations.push(makeOperation(`Final balance for ${currentYear}: ${accruedDays}`));
        
        accruals.set(currentYear, accruedDays);
        currentYear++;
    }


    // NR> fix this its just temp
    // const daysAccrued = Array.from(accruals.values()).reduce((acc, days) => acc + days, 0);
    // console.log("[getAccruedDaysByYearlyFromStartDate] daysAccrued: ", daysAccrued);
    return {
        balance: accruedDays,
        operations
    };
}

// uses anniversary date star and end
// effective date might start it later then anniversary date
// as off date might cut the end date short

const getAccruedDaysByAnniversaryOneYear = (
    policy: PaidTimeOffPolicy, 
    anniversaryStartDate: Date,
    anniversaryEndDate: Date,
    thisYearStartDate: Date,
    asOfEndDate: Date
) => {

   
    let accrualStartDate = thisYearStartDate;
    let accrualEndDate = anniversaryEndDate;

    if (accrualEndDate.getTime() > asOfEndDate.getTime()) {
        accrualEndDate = asOfEndDate;
    }

    console.log(`[getAccruedDaysByAnniversaryOneYear] anniversary start date: ${anniversaryStartDate.getFullYear()}-${anniversaryStartDate.getMonth() + 1}-${anniversaryStartDate.getDate()}`);
    console.log(`[getAccruedDaysByAnniversaryOneYear] anniversary end date: ${anniversaryEndDate.getFullYear()}-${anniversaryEndDate.getMonth() + 1}-${anniversaryEndDate.getDate()}`);
    console.log(`[getAccruedDaysByAnniversaryOneYear] this year start date: ${accrualStartDate.getFullYear()}-${accrualStartDate.getMonth() + 1}-${accrualStartDate.getDate()}`);
    console.log(`[getAccruedDaysByAnniversaryOneYear] this year end date: ${accrualEndDate.getFullYear()}-${accrualEndDate.getMonth() + 1}-${accrualEndDate.getDate()}`);

    // calculate the number of days between the start and end date
    const diffTime = Math.abs(accrualEndDate.getTime() - accrualStartDate.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    // this is if earned by day
    let daysAccrued = Math.floor(diffDays / policy.accrualRate);

    // if its given up front
    // 1. if not on Jan 1 (e.g. a start date of 6/1), then we need to pro rate the amount
    // 2. else give it all up front
    if (policy.accrualType === PTOPolicyAccrualType.UPFRONT) {
        daysAccrued = policy.accrualRate;
    }

    console.log("[getAccruedDaysByAnniversaryOneYear] days worked this anniversary year: ", diffDays);
    console.log("[getAccruedDaysByAnniversaryOneYear] accrualRate: ", policy.accrualRate);
    console.log("[getAccruedDaysByAnniversaryOneYear] daysAccrued: ", daysAccrued);
    
    return daysAccrued;
}